import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Loader2, Printer, AlertTriangle, Presentation, ImagePlus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Lab 9 — AI Presentation + Audio/Video Content.
 * Manual steps 78-85: topic → 6-slide outline → speaker notes → one visual
 * → narration script → review → export.
 * Uses POST /api/labs/9/ask for structured text + existing /api/content/image
 * for the slide visual.
 */
export default function LabPresentation() {
  const [topic, setTopic] = useState('');
  const [slides, setSlides] = useState(null);
  const [notes, setNotes] = useState({});          // { slideIndex: text }
  const [visualPrompt, setVisualPrompt] = useState('');
  const [visualUrl, setVisualUrl] = useState(null);
  const [narration, setNarration] = useState('');
  const [narrationSlide, setNarrationSlide] = useState(1);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');

  const call = async (path, body) => {
    const r = await fetch(`${API}${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify(body),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.detail || `Request failed (${r.status})`);
    return data;
  };

  const makeOutline = async () => {
    setLoading('outline'); setError('');
    try {
      const data = await call('/api/labs/9/ask', { kind: 'outline', context: { topic } });
      setSlides(data.slides || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(''); }
  };

  const makeNotes = async (idx) => {
    const s = slides?.[idx]; if (!s) return;
    setLoading(`notes-${idx}`); setError('');
    try {
      const data = await call('/api/labs/9/ask', { kind: 'speaker_notes', context: { title: s.title, bullets: s.bullets } });
      setNotes(prev => ({ ...prev, [idx]: data.notes || '' }));
    } catch (e) { setError(e.message); }
    finally { setLoading(''); }
  };

  const makeVisual = async () => {
    if (!visualPrompt.trim()) return;
    setLoading('visual'); setError('');
    try {
      const data = await call('/api/content/image', { website: 'saketh.ai', prompt: visualPrompt, style: 'professional' });
      setVisualUrl(data.image_url || data.url || data.result_url || null);
    } catch (e) { setError(e.message); }
    finally { setLoading(''); }
  };

  const makeNarration = async () => {
    const s = slides?.[narrationSlide - 1]; if (!s) return;
    setLoading('narration'); setError('');
    try {
      const data = await call('/api/labs/9/ask', { kind: 'narration_script', context: { title: s.title, bullets: s.bullets } });
      setNarration(data.script || '');
    } catch (e) { setError(e.message); }
    finally { setLoading(''); }
  };

  const askReview = async () => {
    setLoading('review'); setError('');
    try {
      const data = await call('/api/labs/9/ask', {
        kind: 'review',
        context: { topic, slides, notes: Object.values(notes), narration },
      });
      setReview(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(''); }
  };

  const exportPDF = () => {
    const w = window.open('', '_blank'); if (!w) return;
    const safe = s => (s || '').toString().replace(/</g, '&lt;');
    const slidesHtml = (slides || []).map((s, i) => `
      <div style="page-break-inside:avoid;margin-top:14px;border-top:1px solid #e5e7eb;padding-top:10px;">
        <h3>Slide ${s.slide}: ${safe(s.title)}</h3>
        <ul>${(s.bullets || []).map(b => `<li>${safe(b)}</li>`).join('')}</ul>
        ${notes[i] ? `<p><em>Speaker notes:</em> ${safe(notes[i])}</p>` : ''}
      </div>`).join('');
    w.document.write(`<html><head><title>Lab 9 — Deliverable</title>
      <style>body{font-family:-apple-system,sans-serif;padding:28px;max-width:780px;margin:0 auto;color:#111;}
      h1,h2{line-height:1.25;margin-top:1.4em;}
      img{max-width:100%;border-radius:6px;border:1px solid #e5e7eb;margin:8px 0;}
      pre{background:#f8fafc;border:1px solid #e2e8f0;padding:12px;border-radius:8px;white-space:pre-wrap;}
      ul{margin:6px 0;padding-left:1.2em;}</style></head><body>
      <h1>Lab 9 — AI Presentation + Audio/Video Content</h1>
      <p><strong>Topic:</strong> ${safe(topic)}</p>
      <h2>6-Slide Outline</h2>${slidesHtml}
      ${visualUrl ? `<h2>Supporting Visual</h2><img src="${visualUrl}" alt="visual" />` : ''}
      ${narration ? `<h2>Narration Script (slide ${narrationSlide})</h2><pre>${safe(narration)}</pre>` : ''}
      ${review ? `<h2>Review Notes</h2>
        ${review.unsupported_claims?.length ? `<p><strong>Unsupported claims:</strong></p><ul>${review.unsupported_claims.map(x => `<li>${safe(x)}</li>`).join('')}</ul>` : ''}
        ${review.text_overload?.length ? `<p><strong>Text overload fixes:</strong></p><ul>${review.text_overload.map(x => `<li>Slide ${x.slide}: ${safe(x.fix)}</li>`).join('')}</ul>` : ''}
        ${review.copyright_concerns?.length ? `<p><strong>Copyright concerns:</strong></p><ul>${review.copyright_concerns.map(x => `<li>${safe(x)}</li>`).join('')}</ul>` : ''}
        <p><strong>Overall grade:</strong> ${safe(review.overall_grade)}</p>` : ''}
      </body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };

  const Step = ({ n, title, children }) => (
    <section style={{ marginTop: 20, padding: 18, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 28, height: 28, borderRadius: 14, background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 13 }}>{n}</div>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h3>
      </div>
      {children}
    </section>
  );

  const Btn = ({ onClick, disabled, children, slot, dataTestId, variant = 'primary' }) => (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={dataTestId}
      style={{
        padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
        background: variant === 'primary' ? '#6366f1' : 'transparent',
        color: variant === 'primary' ? '#fff' : '#6366f1',
        border: variant === 'primary' ? 'none' : '1px solid #6366f1',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}>
      {loading === slot ? <Loader2 size={13} className="spin-l" /> : children}
    </button>
  );

  const txt = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontFamily: 'inherit', fontSize: 13, lineHeight: 1.5, resize: 'vertical' };

  return (
    <div style={{ padding: '1.5rem 1.5rem 3rem', maxWidth: 1000, margin: '0 auto' }} data-testid="lab-9">
      <Link to="/labs" style={{ color: '#6366f1', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, textDecoration: 'none', marginBottom: 6 }}><ArrowLeft size={14} /> Back to Labs</Link>
      <h1 style={{ margin: '4px 0 2px', fontSize: 24, fontWeight: 600 }}>Lab 9 — AI Presentation + Audio/Video Content</h1>
      <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>Manual steps 78–85. Topic → outline → notes → visual → narration → review → PDF.</p>

      {error && <div data-testid="lab-error" style={{ marginTop: 10, padding: '8px 12px', background: '#fee2e2', border: '1px solid #ef4444', borderRadius: 8, color: '#b91c1c', fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}><AlertTriangle size={13} /> {error}</div>}

      <Step n={1} title="Topic + 6-slide outline (Steps 78–79)">
        <input value={topic} onChange={e => setTopic(e.target.value)} placeholder="e.g. Solar panel efficiency for Indian climates" style={{ ...txt, marginBottom: 10 }} data-testid="lab-topic" />
        <Btn onClick={makeOutline} disabled={!topic.trim() || !!loading} slot="outline" dataTestId="lab-outline-btn"><Presentation size={13} /> Generate 6-slide outline</Btn>
        {slides && (
          <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
            {slides.map((s, i) => (
              <div key={i} style={{ padding: 12, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }} data-testid={`lab-slide-${i + 1}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <strong style={{ fontSize: 14 }}>Slide {s.slide}: {s.title}</strong>
                  <Btn onClick={() => makeNotes(i)} disabled={!!loading} slot={`notes-${i}`} variant="secondary" dataTestId={`lab-notes-btn-${i + 1}`}><Sparkles size={11} /> Notes</Btn>
                </div>
                <ul style={{ margin: '4px 0 0', paddingLeft: 20, fontSize: 13 }}>{(s.bullets || []).map((b, k) => <li key={k}>{b}</li>)}</ul>
                {notes[i] && <div style={{ marginTop: 6, padding: 8, background: '#eef2ff', borderRadius: 6, fontSize: 12, color: '#4338ca' }} data-testid={`lab-notes-${i + 1}`}><em>Speaker notes:</em> {notes[i]}</div>}
              </div>
            ))}
          </div>
        )}
      </Step>

      <Step n={2} title="One supporting visual (Step 81)">
        <input value={visualPrompt} onChange={e => setVisualPrompt(e.target.value)} placeholder="Describe the chart / diagram / image you want" style={{ ...txt, marginBottom: 10 }} data-testid="lab-visual-prompt" />
        <Btn onClick={makeVisual} disabled={!visualPrompt.trim() || !!loading} slot="visual" dataTestId="lab-visual-btn"><ImagePlus size={13} /> Generate visual</Btn>
        {visualUrl && <img src={visualUrl} alt="visual" style={{ marginTop: 10, maxWidth: '100%', borderRadius: 8, border: '1px solid #e5e7eb' }} data-testid="lab-visual-img" />}
      </Step>

      <Step n={3} title="Narration script for one slide (Step 82)">
        <label style={{ fontSize: 12, fontWeight: 500 }}>Which slide?</label>
        <select value={narrationSlide} onChange={e => setNarrationSlide(Number(e.target.value))} style={{ ...txt, marginTop: 4, marginBottom: 10 }} data-testid="lab-narration-slide">
          {(slides || []).map(s => <option key={s.slide} value={s.slide}>Slide {s.slide}: {s.title}</option>)}
        </select>
        <Btn onClick={makeNarration} disabled={!slides?.length || !!loading} slot="narration" dataTestId="lab-narration-btn"><Sparkles size={13} /> Generate narration script</Btn>
        {narration && <textarea rows={5} value={narration} onChange={e => setNarration(e.target.value)} style={{ ...txt, marginTop: 10 }} data-testid="lab-narration-text" />}
      </Step>

      <Step n={4} title="Review the deck (Step 83)">
        <Btn onClick={askReview} disabled={!slides?.length || !!loading} slot="review" variant="secondary" dataTestId="lab-review-btn"><Sparkles size={13} /> Ask the agent to review</Btn>
        {review && (
          <div style={{ marginTop: 10, padding: 12, background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8, fontSize: 13 }}>
            {review.unsupported_claims?.length > 0 && <><strong>Unsupported claims:</strong><ul style={{ margin: '4px 0', paddingLeft: 20 }}>{review.unsupported_claims.map((x, k) => <li key={k}>{x}</li>)}</ul></>}
            {review.text_overload?.length > 0 && <><strong>Text overload fixes:</strong><ul style={{ margin: '4px 0', paddingLeft: 20 }}>{review.text_overload.map((x, k) => <li key={k}>Slide {x.slide}: {x.fix}</li>)}</ul></>}
            {review.copyright_concerns?.length > 0 && <><strong>Copyright concerns:</strong><ul style={{ margin: '4px 0', paddingLeft: 20 }}>{review.copyright_concerns.map((x, k) => <li key={k}>{x}</li>)}</ul></>}
            <div style={{ marginTop: 6 }}><strong>Overall grade:</strong> {review.overall_grade}</div>
          </div>
        )}
      </Step>

      <Step n={5} title="Export deliverable (Steps 84–85)">
        <Btn onClick={exportPDF} disabled={!slides?.length} slot="pdf" variant="secondary" dataTestId="lab-export-pdf"><Printer size={13} /> Save submission PDF</Btn>
      </Step>

      <style>{`.spin-l{animation:spin-l .9s linear infinite}@keyframes spin-l{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
